import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { FC } from 'react';
import { useInventoryState } from '../../../../contexts/inventory.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TStatus } from 'src/apis/finance/finance.interfaces';

const InventoryTransactionDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedTransaction, items } = useInventoryState();

  if (!selectedTransaction) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="inventory-transaction-details-container"
      >
        <Typography>
          {translateText('administration.inventory.noData')}
        </Typography>
      </Box>
    );
  }

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return translateText('common.nA');
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString();
  };

  const relatedItem = items.find(
    (item) => item.id === selectedTransaction.itemId
  );

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="inventory-transaction-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {translateText('administration.inventory.columns.type')}:{' '}
        {selectedTransaction.transactionType}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Transaction Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="transaction-type">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText('administration.inventory.columns.type')}
              </Typography>
              <Chip
                label={selectedTransaction.transactionType}
                color={
                  selectedTransaction.transactionType === 'PURCHASE'
                    ? 'success'
                    : selectedTransaction.transactionType === 'TRANSFER'
                      ? 'info'
                      : selectedTransaction.transactionType === 'DAMAGE'
                        ? 'error'
                        : 'default'
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="transaction-date">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.date')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedTransaction.transactionDate)}
              </Typography>
            </Grid>
            {selectedTransaction.referenceNumber && (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                data-testid="transaction-reference"
              >
                <Typography variant="caption" color="textSecondary">
                  {translateText('administration.inventory.columns.reference')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedTransaction.referenceNumber}
                </Typography>
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="transaction-payment-status"
            >
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.inventory.columns.paymentStatus'
                )}
              </Typography>
              <Chip
                label={selectedTransaction.paymentStatus}
                color={
                  selectedTransaction.paymentStatus === TStatus.PAID
                    ? 'success'
                    : selectedTransaction.paymentStatus === TStatus.UNPAID
                      ? 'warning'
                      : selectedTransaction.paymentStatus === TStatus.PARTIAL
                        ? 'info'
                        : 'default'
                }
                size="small"
              />
            </Grid>
            {selectedTransaction.paymentMode && (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                data-testid="transaction-payment-mode"
              >
                <Typography variant="caption" color="textSecondary">
                  Payment Mode
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedTransaction.paymentMode}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Item & Quantity
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="transaction-item-id">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.itemId')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedTransaction.itemId}
              </Typography>
            </Grid>
            {relatedItem && (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                data-testid="transaction-item-name"
              >
                <Typography variant="caption" color="textSecondary">
                  {translateText('administration.inventory.columns.itemName')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {relatedItem.itemName}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4} data-testid="transaction-quantity">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.quantity')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedTransaction.quantity}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="transaction-unit-price"
            >
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.unitPrice')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                ₹{selectedTransaction.unitPrice.toLocaleString()}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="transaction-total-amount"
            >
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.totalAmount')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                ₹{selectedTransaction.totalAmount.toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Parties Involved
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {selectedTransaction.supplier && (
              <Grid item xs={12} sm={6} data-testid="transaction-supplier">
                <Typography variant="caption" color="textSecondary">
                  {translateText('administration.inventory.columns.supplier')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedTransaction.supplier}
                </Typography>
              </Grid>
            )}
            {selectedTransaction.recipient && (
              <Grid item xs={12} sm={6} data-testid="transaction-recipient">
                <Typography variant="caption" color="textSecondary">
                  Recipient
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedTransaction.recipient}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} data-testid="transaction-performed-by">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.inventory.columns.performedBy')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedTransaction.performedBy}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedTransaction.notes && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {translateText('common.remarks')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} data-testid="transaction-notes">
                <Typography variant="body1" fontWeight="medium">
                  {selectedTransaction.notes}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default InventoryTransactionDetailsView;
